/**
 * Fonction de prix des kits (Lot P3 — CDC §9).
 *
 * PURE et sans dépendance : ce fichier existe en DEUX copies synchronisées —
 * ici (affichage indicatif côté client) et `functions/src/pricing.ts`
 * (vérité serveur, anti-fraude : le montant du navigateur n'est JAMAIS
 * accepté). Toute modification doit être reportée dans les deux, et le
 * barème versionné (commit + changelog CDC).
 *
 * Barème PROVISOIRE (question ouverte Q1 du CDC — ajustable sans impact
 * code) : calibré sur les coûts réels de juillet 2026 (cf.
 * annex_tools/simulateur-prix.html) et le positionnement sous DiyPixelArt.
 */

export type PricingConfig = {
  /** Coût perle (€/perle) — tube HAMA 3000 mono (201-XX). */
  beadCostEUR: number;
  /** Marge de casse/surplus sur les perles (0.08 = +8 %). */
  casseRate: number;
  /** Coût plaque 29×29 (€/plaque, prix de gros Rito ×10). */
  plateCostEUR: number;
  /** Coût par couleur : sachet + temps de tri (€/couleur). */
  colorCostEUR: number;
  /** Forfait boîte + patron imprimé + papier + branding + emballage (€). */
  packagingEUR: number;
  /** Marge brute sur PETIT kit (≤ marginBeadsLo perles). Dégressive ensuite. */
  marginHi: number;
  /** Marge brute sur GRANDE œuvre (≥ marginBeadsHi perles). */
  marginLo: number;
  /** Seuil bas (perles) : en-dessous, marge = marginHi. */
  marginBeadsLo: number;
  /** Seuil haut (perles) : au-dessus, marge = marginLo. */
  marginBeadsHi: number;
  /** Plancher de prix (petits kits — un kit S ne se vend pas 9 €). */
  floorEUR: number;
};

export const DEFAULT_PRICING: PricingConfig = {
  beadCostEUR: 0.0011,
  casseRate: 0.08,
  plateCostEUR: 1.19,
  colorCostEUR: 0.08,
  packagingEUR: 2.6,
  // Marge DÉGRESSIVE, profil « Douce » (validé 07/2026) : on accepte moins de
  // marge sur les grandes œuvres pour ne pas décourager au prix.
  marginHi: 0.6,
  marginLo: 0.45,
  marginBeadsLo: 900,
  marginBeadsHi: 60000,
  floorEUR: 14.9,
};

export type KitFacts = {
  /** Nombre de perles réellement posées (cellules non vides). */
  beads: number;
  /** Plaques 29×29 entamées. */
  plates: number;
  /** Couleurs distinctes utilisées (= sachets). */
  colors: number;
};

export type PriceBreakdown = {
  beadsEUR: number;
  platesEUR: number;
  colorsEUR: number;
  packagingEUR: number;
  cogsEUR: number;
  /** Prix TTC affiché (arrondi psychologique à ,90). Franchise TVA : TTC = HT. */
  priceEUR: number;
  /** Prix en centimes entiers — LA valeur à passer aux APIs (jamais de float). */
  priceCents: number;
};

/**
 * Marge brute DÉGRESSIVE : forte sur les petits kits (le prix est anodin),
 * plus faible sur les grandes œuvres (pour ne pas décourager à l'achat).
 * Interpolation linéaire sur log(perles) entre les deux ancres, clampée.
 */
export function marginFor(beads: number, cfg: PricingConfig = DEFAULT_PRICING): number {
  const b = Number.isFinite(beads) && beads > 0 ? beads : 1;
  const lo = cfg.marginBeadsLo;
  const hi = cfg.marginBeadsHi;
  if (hi <= lo) return cfg.marginHi;
  const t = (Math.log(b) - Math.log(lo)) / (Math.log(hi) - Math.log(lo));
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return cfg.marginHi + (cfg.marginLo - cfg.marginHi) * c;
}

export function computeKitPrice(facts: KitFacts, cfg: PricingConfig = DEFAULT_PRICING): PriceBreakdown {
  if (!Number.isFinite(facts.beads) || facts.beads < 0) {
    throw new Error(`beads invalide : ${facts.beads}`);
  }
  if (!Number.isFinite(facts.plates) || facts.plates < 0) {
    throw new Error(`plates invalide : ${facts.plates}`);
  }
  if (!Number.isFinite(facts.colors) || facts.colors < 0) {
    throw new Error(`colors invalide : ${facts.colors}`);
  }
  const beadsEUR = facts.beads * cfg.beadCostEUR * (1 + cfg.casseRate);
  const platesEUR = facts.plates * cfg.plateCostEUR;
  const colorsEUR = facts.colors * cfg.colorCostEUR;
  const cogsEUR = beadsEUR + platesEUR + colorsEUR + cfg.packagingEUR;

  const raw = cogsEUR / (1 - marginFor(facts.beads, cfg));
  const floored = Math.max(raw, cfg.floorEUR);
  const priceEUR = roundTo90(floored);

  return {
    beadsEUR: round2(beadsEUR),
    platesEUR: round2(platesEUR),
    colorsEUR: round2(colorsEUR),
    packagingEUR: round2(cfg.packagingEUR),
    cogsEUR: round2(cogsEUR),
    priceEUR,
    priceCents: Math.round(priceEUR * 100),
  };
}

/** Arrondi psychologique : x,90 € (jamais en dessous de la valeur brute - 1). */
function roundTo90(v: number): number {
  const base = Math.floor(v);
  const candidate = base + 0.9;
  // Si v dépasse déjà x,90, passer au palier suivant.
  return v <= candidate ? candidate : base + 1.9;
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Poids estimé du colis (g) — pour le tarif transporteur du Draft Order. */
export function estimateWeightGrams(facts: KitFacts): number {
  const BEAD_G = 0.067; // HAMA Midi ~67 g / 1000
  const PLATE_G = 30;
  const BOX_AND_PAPERS_G = 180; // boîte + patron + papier + sachets
  return Math.round(facts.beads * BEAD_G + facts.plates * PLATE_G + BOX_AND_PAPERS_G);
}
